import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { ChevronLineIcon } from "@/Icons/v2/ChevronLine";
import { DownloadIcon } from "@/Icons/v2/Download";
import { useConfig } from "@/api/hooks/useConfig";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { twMerge } from "tailwind-merge";

import { useConfirmationDialog } from "./Overlays/ConfirmationDialog";

type Props = {
  nrSelected: number;
  nrTotal?: number;
  areAllSelected?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  disabledText?: string;
  exportExcelFunction: () => void;
  exportCsvFunction: () => void;
  showConfirmationDialog?: boolean;
};

export const ExportButton = ({
  nrSelected,
  areAllSelected,
  isLoading,
  disabled,
  disabledText,
  exportExcelFunction,
  exportCsvFunction,
  showConfirmationDialog,
}: Props) => {
  const { t } = useTrans();
  const { isMWM } = useConfig();
  const { openConfirmationDialog } = useConfirmationDialog();

  const buttonText = useMemo(() => {
    if (!nrSelected || areAllSelected) {
      return t("actions:exportAll");
    }

    if (nrSelected === 1) {
      return t("actions:exportRow", { nrRows: nrSelected });
    }

    if (nrSelected) {
      return t("actions:exportRows", { nrRows: nrSelected });
    }
  }, [nrSelected, areAllSelected, t]);

  const handleOptionClick = useCallback(
    (close: () => void, onClickFunction: () => void) => {
      const exportFunction = () => {
        close();
        onClickFunction();
      };

      if (showConfirmationDialog) {
        openConfirmationDialog(
          t("exportLabels:slowExportWarningTitle"),
          exportFunction,
          t("exportLabels:slowExportWarningDescription", {
            timespan: t(isMWM ? "periods:month" : "periods:year"),
          }),
        );
      } else {
        exportFunction();
      }
    },
    [showConfirmationDialog, openConfirmationDialog, isMWM, t],
  );

  const options = useMemo(
    () => [
      {
        key: "excel",
        label: t("actions:exportExcel"),
        onClickFunction: exportExcelFunction,
      },
      {
        key: "csv",
        label: t("actions:exportCsv"),
        onClickFunction: exportCsvFunction,
      },
    ],
    [exportExcelFunction, exportCsvFunction, t],
  );

  const inactive = useMemo(() => isLoading || disabled, [isLoading, disabled]);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={twMerge(
              "bg-lime flex w-fit items-center gap-2 px-[18px] py-[10px] text-[13.5px] font-semibold",
              CLIPPED_BUTTON_STYLE,
              disabled && "bg-grey1",
            )}
            disabled={inactive}
          >
            <DownloadIcon size={15} />
            {isLoading && <LoadingIndicator />}
            {!isLoading && disabled ? disabledText : buttonText}
            {!inactive && (
              <div>
                <ChevronLineIcon
                  className={twMerge(open && "rotate-180 transition-transform")}
                  color="#1D1C1C"
                />
              </div>
            )}
          </PopoverButton>
          <PopoverPanel className="absolute right-0 z-10">
            <div className="min-w-[150px] border bg-white shadow-md">
              {options.map((el) => (
                <button
                  key={el.key}
                  className="hover:bg-grey1 flex h-12 w-full items-center px-4 text-sm"
                  onClick={() => handleOptionClick(close, el.onClickFunction)}
                >
                  {el.label}
                </button>
              ))}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
